import React, { useRef, useState, useEffect, useCallback, useLayoutEffect } from 'react';
import { Stage, Layer, Image as KonvaImage, Line } from 'react-konva';
import Konva from 'konva';
import { useTranslation } from '../../hooks/useTranslation';

interface InpaintCanvasProps {
  imageUrl: string; // Source image (can be data URL or regular URL)
  onMaskChange: (maskBase64: string) => void;
  disabled?: boolean;
  className?: string;
}

interface LineData {
  points: number[];
  brushSize: number;
}

const InpaintCanvas: React.FC<InpaintCanvasProps> = ({
  imageUrl,
  onMaskChange,
  disabled = false,
  className = '',
}) => {
  const { t } = useTranslation();
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [lines, setLines] = useState<LineData[]>([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [brushSize, setBrushSize] = useState(30);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [imageLoadError, setImageLoadError] = useState(false);
  const stageRef = useRef<Konva.Stage>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Calculate dimensions based on image and container
  const calculateDimensions = useCallback((img: HTMLImageElement) => {
    // Validate image has valid dimensions
    if (!img.width || !img.height || img.width <= 0 || img.height <= 0) {
      // Return default dimensions that will be caught by hasValidDimensions check
      return { width: 0, height: 0 };
    }

    // Get container width, with fallback
    const containerWidth = containerRef.current?.clientWidth || 600;
    // Use a reasonable fallback if container width is still 0
    const effectiveWidth = containerWidth > 0 ? containerWidth : 600;

    // Responsive max height: 60% of viewport, min 300px, max 600px
    const maxHeight = Math.min(600, Math.max(300, window.innerHeight * 0.6));
    const aspectRatio = img.width / img.height;

    let width = effectiveWidth;
    let height = width / aspectRatio;

    if (height > maxHeight) {
      height = maxHeight;
      width = height * aspectRatio;
    }

    // Ensure minimum dimensions
    width = Math.max(width, 200);
    height = Math.max(height, 150);

    // Final validation to prevent NaN/Infinity
    if (!Number.isFinite(width) || !Number.isFinite(height)) {
      return { width: 400, height: 300 }; // Safe fallback
    }

    return { width, height };
  }, []);

  // Detect image type from base64 header
  const detectImageType = useCallback((base64: string): string => {
    // Check magic bytes in base64
    if (base64.startsWith('/9j/')) return 'image/jpeg';
    if (base64.startsWith('iVBORw0KGgo')) return 'image/png';
    if (base64.startsWith('R0lGOD')) return 'image/gif';
    if (base64.startsWith('UklGR')) return 'image/webp';
    // Default to PNG if unknown
    return 'image/png';
  }, []);

  // Load image
  useEffect(() => {
    if (!imageUrl) return;

    const img = new window.Image();
    setImageLoadError(false);

    // Only set crossOrigin for external URLs, not for data URLs
    const isDataUrl = imageUrl.startsWith('data:');
    const isBase64Only =
      !imageUrl.startsWith('data:') &&
      !imageUrl.startsWith('http') &&
      !imageUrl.startsWith('blob:');

    if (!isDataUrl && !isBase64Only) {
      img.crossOrigin = 'anonymous';
    }

    img.onload = () => {
      // Validate image dimensions
      if (!img.width || !img.height || img.width <= 0 || img.height <= 0) {
        console.error('InpaintCanvas: Image loaded with invalid dimensions', {
          width: img.width,
          height: img.height,
        });
        setImageLoadError(true);
        return;
      }
      setImage(img);
      const dims = calculateDimensions(img);
      setDimensions(dims);
    };

    img.onerror = (e) => {
      console.error('InpaintCanvas: Failed to load image', e);
      setImageLoadError(true);
    };

    // If imageUrl is raw base64 (no prefix), add the data URL prefix
    // This happens when ImageUploader strips the prefix
    if (isBase64Only && imageUrl.length > 0) {
      // Detect the actual image type from base64 header
      const mimeType = detectImageType(imageUrl);
      img.src = `data:${mimeType};base64,${imageUrl}`;
    } else {
      img.src = imageUrl;
    }
  }, [imageUrl, calculateDimensions, detectImageType]);

  // Recalculate dimensions when container resizes
  useLayoutEffect(() => {
    if (!image) return;

    const handleResize = () => {
      const dims = calculateDimensions(image);
      setDimensions(dims);
    };

    // Initial calculation after mount
    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [image, calculateDimensions]);

  const exportMask = useCallback((): void => {
    if (!stageRef.current || !image) return;

    // Create a temporary canvas at the original image size
    const canvas = document.createElement('canvas');
    canvas.width = image.width;
    canvas.height = image.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Fill with black background
    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Calculate scale factor
    const scaleX = image.width / dimensions.width;
    const scaleY = image.height / dimensions.height;

    // Draw white lines for mask
    ctx.strokeStyle = '#FFFFFF';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    lines.forEach((line) => {
      ctx.lineWidth = line.brushSize * scaleX;
      ctx.beginPath();

      for (let i = 0; i < line.points.length; i += 2) {
        const x = line.points[i] * scaleX;
        const y = line.points[i + 1] * scaleY;

        if (i === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      }
      ctx.stroke();
    });

    // Convert to base64
    const base64 = canvas.toDataURL('image/png');
    onMaskChange(base64);
  }, [image, dimensions.width, dimensions.height, lines, onMaskChange]);

  // Export mask whenever lines change
  useEffect(() => {
    if (!image || lines.length === 0) {
      onMaskChange('');
      return;
    }

    exportMask();
  }, [lines, image, onMaskChange, exportMask]);

  const handleMouseDown = (e: Konva.KonvaEventObject<MouseEvent>): void => {
    if (disabled) return;
    setIsDrawing(true);
    const pos = e.target.getStage()?.getPointerPosition();
    if (!pos) return;
    setLines([...lines, { points: [pos.x, pos.y], brushSize }]);
  };

  const handleMouseMove = (e: Konva.KonvaEventObject<MouseEvent>): void => {
    if (!isDrawing || disabled) return;

    const stage = e.target.getStage();
    const point = stage?.getPointerPosition();
    if (!point) return;

    const lastLine = lines[lines.length - 1];
    lastLine.points = lastLine.points.concat([point.x, point.y]);

    // Replace the last line
    setLines([...lines.slice(0, -1), lastLine]);
  };

  const handleMouseUp = (): void => {
    setIsDrawing(false);
  };

  const handleClear = (): void => {
    setLines([]);
  };

  const handleUndo = (): void => {
    setLines(lines.slice(0, -1));
  };

  // Show error state
  if (imageLoadError) {
    return (
      <div
        className={`flex flex-col items-center justify-center h-64 bg-red-50 border-2 border-red-200 rounded-lg ${className}`}
      >
        <p className="text-red-600 font-medium">
          {t('image.inpaint.loadError', { defaultValue: 'Failed to load image' })}
        </p>
        <p className="text-red-500 text-sm mt-1">
          {t('image.inpaint.loadErrorHint', {
            defaultValue: 'Please try uploading a different image',
          })}
        </p>
      </div>
    );
  }

  // Show loading state
  if (!image) {
    return (
      <div
        className={`flex items-center justify-center h-64 bg-gray-100 dark:bg-secondary-800 rounded-lg ${className}`}
      >
        <div className="flex flex-col items-center gap-2">
          <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
          <p className="text-gray-500 dark:text-gray-400">
            {t('image.imageUploader.loading', { defaultValue: 'Loading image...' })}
          </p>
        </div>
      </div>
    );
  }

  // Show canvas only when we have valid dimensions (check for 0, NaN, Infinity)
  const hasValidDimensions =
    Number.isFinite(dimensions.width) &&
    Number.isFinite(dimensions.height) &&
    dimensions.width > 0 &&
    dimensions.height > 0;

  if (!hasValidDimensions) {
    return (
      <div
        className={`flex items-center justify-center h-64 bg-gray-100 dark:bg-secondary-800 rounded-lg ${className}`}
      >
        <div className="flex flex-col items-center gap-2">
          <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
          <p className="text-gray-500 dark:text-gray-400">
            {t('image.inpaint.calculatingSize', { defaultValue: 'Preparing canvas...' })}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      {/* Controls */}
      <div className="mb-4 space-y-4">
        <div>
          <label className="label-gray">
            {t('image.inpaint.brushSize', { defaultValue: 'Brush Size' })}: {brushSize}px
          </label>
          <input
            type="range"
            min="5"
            max="100"
            value={brushSize}
            onChange={(e) => setBrushSize(Number(e.target.value))}
            disabled={disabled}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer disabled:opacity-50"
          />
        </div>

        <div className="flex gap-2">
          <button
            onClick={handleUndo}
            disabled={disabled || lines.length === 0}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:bg-gray-300 disabled:text-gray-500 dark:bg-secondary-600 dark:hover:bg-secondary-500 dark:disabled:bg-secondary-700 dark:disabled:text-secondary-500 disabled:cursor-not-allowed transition-colors"
          >
            {t('image.inpaint.undo')}
          </button>
          <button
            onClick={handleClear}
            disabled={disabled || lines.length === 0}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-300 disabled:text-gray-500 dark:bg-red-700 dark:hover:bg-red-600 dark:disabled:bg-secondary-700 dark:disabled:text-secondary-500 disabled:cursor-not-allowed transition-colors"
          >
            {t('image.inpaint.clear')}
          </button>
        </div>

        <p className="text-sm text-gray-600 dark:text-gray-400">
          {t('image.inpaint.drawMask', {
            defaultValue: 'Draw on the image to mark areas to change',
          })}
        </p>
      </div>

      {/* Canvas */}
      <div
        ref={containerRef}
        className="border-2 border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden bg-gray-50 dark:bg-secondary-700"
      >
        <Stage
          ref={stageRef}
          width={dimensions.width}
          height={dimensions.height}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          style={{ cursor: disabled ? 'default' : 'crosshair' }}
        >
          <Layer>
            {/* Background Image */}
            <KonvaImage image={image} width={dimensions.width} height={dimensions.height} />

            {/* Drawn Lines */}
            {lines.map((line, lineIndex) => (
              <Line
                key={`line-${lineIndex}-${line.points.slice(0, 4).join('-')}`}
                points={line.points}
                stroke="#FFFFFF"
                strokeWidth={line.brushSize}
                tension={0.5}
                lineCap="round"
                lineJoin="round"
                globalCompositeOperation="source-over"
                opacity={0.7}
              />
            ))}
          </Layer>
        </Stage>
      </div>
    </div>
  );
};

export default InpaintCanvas;
